import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { ToastContainer, toast } from 'react-toastify';
import { motion } from 'framer-motion';
import { ClipLoader } from 'react-spinners';
import styled from 'styled-components';
import 'react-toastify/dist/ReactToastify.css';
import backgroundImage from '../background.webp';
import cardBackgroundImage from '../cardbackground.webp';
import buttonImage from '../buttonimage.webp';
import PrivacyPolicy from './PrivacyPolicy';

export const validateAddressInput = (inputData) => {
    const { billingAddress, city, state, zipCode } = inputData;
    if (!billingAddress || !city || !state || !zipCode) {
        return { valid: false, reason: 'Missing address fields' };
    }

    const stateMap = {
        'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR', 'california': 'CA',
        'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE', 'florida': 'FL', 'georgia': 'GA',
        'hawaii': 'HI', 'idaho': 'ID', 'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA',
        'kansas': 'KS', 'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD',
        'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS',
        'missouri': 'MO', 'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV', 'new hampshire': 'NH',
        'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY', 'north carolina': 'NC',
        'north dakota': 'ND', 'ohio': 'OH', 'oklahoma': 'OK', 'oregon': 'OR', 'pennsylvania': 'PA',
        'rhode island': 'RI', 'south carolina': 'SC', 'south dakota': 'SD', 'tennessee': 'TN',
        'texas': 'TX', 'utah': 'UT', 'vermont': 'VT', 'virginia': 'VA', 'washington': 'WA',
        'west virginia': 'WV', 'wisconsin': 'WI', 'wyoming': 'WY', 'district of columbia': 'DC'
    };

    const normalizedState = state.trim().toLowerCase();
    const stateCode = stateMap[normalizedState] || state.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(stateCode)) {
        return { valid: false, reason: 'State must be a 2-character code (e.g., NH)' };
    }

    const cleanZipCode = zipCode.trim().split('-')[0];
    if (!/^\d{5}$/.test(cleanZipCode)) {
        return { valid: false, reason: 'Zip code must be 5 digits (e.g., 03243)' };
    }

    return {
        valid: true,
        normalizedFormData: {
            billingAddress: billingAddress.trim(),
            city: city.trim(),
            state: stateCode,
            zipCode: cleanZipCode,
        },
    };
};

const getAuthRedirectUrl = (path = '/myAccount') => {
    const liveBaseUrl = 'https://www.companionjulie.com';
    const localBaseUrl = 'http://localhost:3000';
    const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const baseUrl = isLocalDev ? localBaseUrl : liveBaseUrl;
    return `${baseUrl}${path}`;
};

const SignUp = ({ membershipType, setCurrentPage }) => {
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        nickname: '',
        billingAddress: '',
        city: '',
        state: '',
        zipCode: '',
        couponCode: '',
    });
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedMembership, setSelectedMembership] = useState(membershipType || 'Free');
    const [tooltip, setTooltip] = useState('');
    const [addressValid, setAddressValid] = useState(true);
    const [addressError, setAddressError] = useState('');
    const [couponCodeValue, setCouponCodeValue] = useState(null);
    const [privacyChecked, setPrivacyChecked] = useState(false);
    const [showPrivacyModal, setShowPrivacyModal] = useState(false);

    const membershipDescriptions = {
        Free: 'This option is available only for a limited time.  In the future all subscriptions will be paid subscriptions. Meanwhile, Feel free to enjoy Free Access to Journal, Oracle, To-Do, Meditation, and one Free Session per month.',
        Monthly: 'Billed Automatically Each Month Unless Cancelled, No Refunds, Full Access to Unlimited New Sessions, Resume Last Session, Quick Reference (self healing tools), Journal, Meditation, To-Do, and Community Chat Room after three consecutive months of App Subscription. Change Membership Type at any time.',
        Yearly: 'Billed Automatically Each Year Unless Cancelled, No Refunds, Full Access to Unlimited New Sessions, Resume Last Session, Quick Reference (self healing tools), Journal, Meditation, To-Do, and Community Chat Room after three consecutive months of App Subscription. Change Membership Type at any time.',
        Gift: 'Switched to Free Membership Automatically After Coupon Code Expires, Full Access to Unlimited New Sessions, Resume Last Session, Quick Reference (self healing tools), Journal, Meditation, To-Do, and Community Chat Room. Change Membership Type at any time. Must change to paid subscription after Free membership is no longer available.',
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));

        if (['billingAddress', 'city', 'state', 'zipCode'].includes(name)) {
            setAddressValid(true);
            setAddressError('');
        }
    };

    const validateAddress = async () => {
        try {
            const precheck = validateAddressInput(formData);
            if (!precheck.valid) {
                setAddressValid(false);
                setAddressError(precheck.reason);
                toast.error(precheck.reason);
                return { valid: false, reason: precheck.reason };
            }

            const { billingAddress, city, state, zipCode } = precheck.normalizedFormData;
            const response = await fetch('https://zxvjqkrfvproyblrjcst.supabase.co/functions/v1/verify-usps-address-v6', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inp4dmpxa3JmdnByb3libHJqY3N0Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDMyODU0MTYsImV4cCI6MjA1ODg2MTQxNn0.OKhbVDZBzdAybrDDTJ-lhB90euEbTioNggjSVDK7uJU',
                },
                body: JSON.stringify({
                    addressLine1: billingAddress,
                    city,
                    state,
                    zipCode,
                }),
            });
            if (!response.ok) {
                const errorText = await response.text();
                console.error('Address validation failed:', response.status, errorText);
                setAddressValid(false);
                toast.error('Invalid address. Please correct and try again.');
                return { valid: false, reason: 'Invalid address' };
            }
            const result = await response.json();
            if (result.status === 'success') {
                setFormData((prev) => ({
                    ...prev,
                    billingAddress: result.address.addressLine1,
                    city: result.address.city,
                    state: result.address.state,
                    zipCode: result.address.zipCode,
                }));
                setAddressValid(true);
                setAddressError('');
                return { valid: true, reason: null };
            }

            console.error('Invalid address response:', result);
            setAddressValid(false);
            setAddressError('Invalid address. Please correct and try again.');
            toast.error('Invalid address. Please correct and try again.');
            return { valid: false, reason: 'Invalid address' };
        } catch (err) {
            console.error('Address validation error:', err.message);
            setAddressValid(false);
            setAddressError('Invalid address. Please correct and try again.');
            toast.error('Invalid address. Please correct and try again.');
            return { valid: false, reason: 'Invalid address' };
        }
    };

    const validateCoupon = async () => {
        try {
            const { couponCode } = formData;
            if (!couponCode) {
                toast.error('Please enter a coupon code.');
                return null;
            }
            const normalizedCode = couponCode.trim();
            console.log('Validating coupon code:', normalizedCode);
            const { data, error } = await supabase
                .schema('public')
                .from('coupons')
                .select('code, used, user_id, redeemed_at, duration')
                .eq('code', normalizedCode)
                .eq('used', false)
                .is('user_id', null)
                .maybeSingle();
            if (error || !data) {
                console.error('Coupon validation error:', error?.message || 'No valid coupon found', 'Code:', normalizedCode);
                toast.error('Invalid or used coupon code.');
                return null;
            }
            console.log('Coupon validated:', data);
            setCouponCodeValue(normalizedCode);
            return { membershipType: 'Gift Subscription', membership: 'GIFT' };
        } catch (err) {
            console.error('Coupon validation error:', err.message, err.stack);
            toast.error('Failed to validate coupon.');
            return null;
        }
    };

    const handleSignUp = async () => {
        setLoading(true);
        toast.info('Processing sign-up... Please check your email to confirm.', {
            icon: <ClipLoader size={20} color="#4B5563" />,
            autoClose: 10000,
        });

        try {
            const addressCheck = await validateAddress();
            if (!addressCheck.valid) {
                setLoading(false);
                return;
            }

            const { email, password, confirmPassword, firstName, lastName, billingAddress, city, state, zipCode } = formData;
            if (!email || !password || !confirmPassword || !firstName || !lastName) {
                toast.error('Please fill in all required fields.');
                setLoading(false);
                return;
            }
            if (password !== confirmPassword) {
                toast.error('Passwords do not match.');
                setLoading(false);
                return;
            }
            if (!selectedMembership) {
                toast.error('Please select a membership type.');
                setLoading(false);
                return;
            }
            if (!privacyChecked) {
                toast.error('You must agree to the Privacy Policy.');
                setLoading(false);
                return;
            }

            const encryptionKey = crypto.randomUUID();
            let membershipType = '';
            let membership = '';

            console.log('Initiating supabase.auth.signUp with email:', email);
            const { data, error } = await supabase.auth.signUp({
                email,
                password,
                options: {
                    emailRedirectTo: getAuthRedirectUrl('/myAccount')
                }
            });
            console.log('supabase.auth.signUp response:', { data, error });

            if (error) {
                console.error('Sign-up error:', error.message);
                if (error.message.includes('User already registered')) {
                    toast.error('Email already registered.');
                } else {
                    toast.error('Sign-up failed: ' + error.message);
                }
                setLoading(false);
                return;
            }
            if (!data.user) {
                console.error('No user data returned from sign-up');
                toast.error('Sign-up failed: No user data returned.');
                setLoading(false);
                return;
            }

            let extraProfileFields = {};
            if (selectedMembership === 'Free') {
                membershipType = 'Free Subscription';
                membership = 'None';
            } else if (selectedMembership === 'Gift') {
                const giftValidation = await validateCoupon();
                if (!giftValidation) {
                    setLoading(false);
                    return;
                }
                membershipType = giftValidation.membershipType;
                membership = giftValidation.membership;

                // Fetch coupon duration and set subscription_start_date/current_period_end/membership_end_date
                const { data: couponData, error: couponError } = await supabase
                    .schema('public')
                    .from('coupons')
                    .select('duration')
                    .eq('code', formData.couponCode.trim())
                    .single();
                if (couponError || !couponData) {
                    toast.error('Failed to fetch coupon duration.');
                    setLoading(false);
                    return;
                }
                const durationDays = parseInt(couponData.duration, 10);
                if (isNaN(durationDays)) {
                    toast.error('Invalid coupon duration.');
                    setLoading(false);
                    return;
                }
                const subscriptionStartDate = new Date();
                const currentPeriodEnd = new Date(subscriptionStartDate.getTime() + durationDays * 24 * 60 * 60 * 1000);
                extraProfileFields = {
                    subscription_start_date: subscriptionStartDate.toISOString(),
                    current_period_end: currentPeriodEnd.toISOString(),
                    membership_end_date: currentPeriodEnd.toISOString() // <-- add this line
                };
            } else if (selectedMembership === 'Monthly' || selectedMembership === 'Yearly') {
                membershipType = selectedMembership === 'Monthly' ? 'Monthly Subscription' : 'Yearly Subscription';
                membership = selectedMembership === 'Monthly' ? 'Monthly' : 'Yearly';
            }

            const { error: profileError } = await supabase
                .from('user_profiles')
                .insert({
                    id: data.user.id,
                    email,
                    first_name: firstName,
                    last_name: lastName,
                    nickname: formData.nickname || null,
                    billing_address: billingAddress,
                    city,
                    state,
                    zip_code: zipCode,
                    membership_type: membershipType,
                    membership,
                    created_at: new Date().toISOString(),
                    text_number: null,
                    nickname_changed: false,
                    font_color: null,
                    bubble_color: null,
                    status_words: null,
                    encryption_key: encryptionKey,
                    privacy_policy_accepted: true,
                    ...extraProfileFields // <-- Add subscription_start_date and current_period_end if Gift
                });
            if (profileError) {
                console.error('Profile insert error:', profileError.message);
                toast.error('Failed to save profile: ' + profileError.message);
                setLoading(false);
                return;
            }

            if (selectedMembership === 'Monthly' || selectedMembership === 'Yearly') {
                const { error: pendingError } = await supabase
                    .from('pending_subscriptions')
                    .insert({
                        id: data.user.id,
                        email,
                        membership_type: membershipType,
                        created_at: new Date().toISOString(),
                    });
                if (pendingError) {
                    console.error('Pending subscription insert error:', pendingError.message);
                    toast.error('Failed to save pending subscription: ' + pendingError.message);
                    setLoading(false);
                    return;
                }
            }

            toast.success('Success! Profile created. Please check your email to confirm.');
        } catch (err) {
            console.error('Unexpected error during sign-up:', err.message, err.stack);
            toast.error('Sign-up failed: Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // On mount, if already authenticated and confirmed, redirect to myAccount
        const checkSession = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (session && session.user && session.user.email_confirmed_at) {
                setCurrentPage('myAccount');
            }
        };
        checkSession();

        const { data: subscription } = supabase.auth.onAuthStateChange(async (_event, session) => {
            if (session && session.user && session.user.email_confirmed_at) {
                console.log('Email confirmed for user:', session.user.id);
                if (couponCodeValue && selectedMembership === 'Gift') {
                    console.log('Updating coupon:', couponCodeValue, 'with user_id:', session.user.id);
                    const { data: couponData, error: couponError } = await supabase
                        .schema('public')
                        .from('coupons')
                        .select('duration')
                        .eq('code', couponCodeValue)
                        .single();
                    if (couponError || !couponData) {
                        console.error('Coupon duration fetch error:', couponError?.message);
                        toast.error('Failed to fetch coupon duration.');
                        return;
                    }
                    const durationDays = parseInt(couponData.duration, 10);
                    if (isNaN(durationDays)) {
                        console.error('Invalid coupon duration:', couponData.duration);
                        toast.error('Invalid coupon duration.');
                        return;
                    }
                    const redeemedAt = new Date();
                    const endDate = new Date(redeemedAt.getTime() + durationDays * 24 * 60 * 60 * 1000);
                    const { error: updateError } = await supabase
                        .schema('public')
                        .from('coupons')
                        .update({
                            used: true,
                            redeemed_at: redeemedAt.toISOString(),
                            user_id: session.user.id,
                            end_date: endDate.toISOString()
                        })
                        .eq('code', couponCodeValue);
                    if (updateError) {
                        console.error('Coupon update error:', updateError.message, updateError.details);
                        toast.error('Failed to mark coupon as used: ' + updateError.message);
                    } else {
                        console.log('Coupon updated successfully for user_id:', session.user.id, 'end_date:', endDate.toISOString());
                        setCouponCodeValue(null);
                    }
                }
                setCurrentPage('myAccount');
            }
        });
        const poll = setInterval(async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (session && session.user && session.user.email_confirmed_at) {
                console.log('Poll: Email confirmed for user:', session.user.id);
                if (couponCodeValue && selectedMembership === 'Gift') {
                    console.log('Poll: Updating coupon:', couponCodeValue, 'with user_id:', session.user.id);
                    const { data: couponData, error: couponError } = await supabase
                        .schema('public')
                        .from('coupons')
                        .select('duration')
                        .eq('code', couponCodeValue)
                        .single();
                    if (couponError || !couponData) {
                        console.error('Coupon duration fetch error:', couponError?.message);
                        toast.error('Failed to fetch coupon duration.');
                        return;
                    }
                    const durationDays = parseInt(couponData.duration, 10);
                    if (isNaN(durationDays)) {
                        console.error('Invalid coupon duration:', couponData.duration);
                        toast.error('Invalid coupon duration.');
                        return;
                    }
                    const redeemedAt = new Date();
                    const endDate = new Date(redeemedAt.getTime() + durationDays * 24 * 60 * 60 * 1000);
                    const { error: updateError } = await supabase
                        .schema('public')
                        .from('coupons')
                        .update({
                            used: true,
                            redeemed_at: redeemedAt.toISOString(),
                            user_id: session.user.id,
                            end_date: endDate.toISOString()
                        })
                        .eq('code', couponCodeValue);
                    if (updateError) {
                        console.error('Coupon update error:', updateError.message, updateError.details);
                        toast.error('Failed to mark coupon as used: ' + updateError.message);
                    } else {
                        console.log('Coupon updated successfully for user_id:', session.user.id, 'end_date:', endDate.toISOString());
                        setCouponCodeValue(null);
                    }
                }
                setCurrentPage('myAccount');
            }
        }, 3000);
        return () => {
            subscription?.unsubscribe?.();
            clearInterval(poll);
        };
    }, [setCurrentPage, couponCodeValue, selectedMembership]);

    const handleTogglePasswordVisibility = () => {
        setShowPassword((prev) => !prev);
    };

    return (
        <Container>
            <ToastContainer />
            <SubscriptionCard
                as={motion.div}
                initial={{ opacity: 0, y: -50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <h3>Membership Options</h3>
                <Row>
                    <Stack>
                        <Option>
                            <input
                                type="radio"
                                id="Free"
                                name="membership"
                                value="Free"
                                checked={selectedMembership === "Free"}
                                onChange={() => setSelectedMembership("Free")}
                                onMouseEnter={() => setTooltip(membershipDescriptions["Free"])}
                                onMouseLeave={() => setTooltip("")}
                            />
                            <label htmlFor="Free">Free Membership</label>
                        </Option>
                        <Option>
                            <input
                                type="radio"
                                id="Monthly"
                                name="membership"
                                value="Monthly"
                                checked={selectedMembership === "Monthly"}
                                onChange={() => setSelectedMembership("Monthly")}
                                onMouseEnter={() => setTooltip(membershipDescriptions["Monthly"])}
                                onMouseLeave={() => setTooltip("")}
                            />
                            <label htmlFor="Monthly">Monthly Membership</label>
                        </Option>
                        <Option>
                            <input
                                type="radio"
                                id="Yearly"
                                name="membership"
                                value="Yearly"
                                checked={selectedMembership === "Yearly"}
                                onChange={() => setSelectedMembership("Yearly")}
                                onMouseEnter={() => setTooltip(membershipDescriptions["Yearly"])}
                                onMouseLeave={() => setTooltip("")}
                            />
                            <label htmlFor="Yearly">Yearly Membership</label>
                        </Option>
                    </Stack>
                    <Stack>
                        <Option>
                            <input
                                type="radio"
                                id="Gift"
                                name="membership"
                                value="Gift"
                                checked={selectedMembership === "Gift"}
                                onChange={() => setSelectedMembership("Gift")}
                                onMouseEnter={() => setTooltip(membershipDescriptions["Gift"])}
                                onMouseLeave={() => setTooltip("")}
                            />
                            <label htmlFor="Gift">Gift Membership</label>
                        </Option>
                        {selectedMembership === "Gift" && (
                            <Input
                                type="text"
                                name="couponCode"
                                placeholder="Enter Coupon Code"
                                value={formData.couponCode}
                                onChange={handleInputChange}
                            />
                        )}
                    </Stack>
                </Row>
                <Description>
                    {tooltip || membershipDescriptions[selectedMembership]}
                </Description>
            </SubscriptionCard>

            <Card
                as={motion.div}
                initial={{ opacity: 0, y: -50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0 }}
            >
                <MembershipTitle>Sign up for your {selectedMembership} Subscription</MembershipTitle>
                <Row>
                    <Input
                        type="email"
                        name="email"
                        placeholder="Email"
                        value={formData.email}
                        onChange={handleInputChange}
                    />
                    <PasswordStack>
                        <Input
                            type={showPassword ? 'text' : 'password'}
                            name="password"
                            placeholder="Password"
                            value={formData.password}
                            onChange={handleInputChange}
                        />
                        <Input
                            type={showPassword ? 'text' : 'password'}
                            name="confirmPassword"
                            placeholder="Confirm Password"
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                        />
                        <ToggleButton onClick={handleTogglePasswordVisibility}>
                            {showPassword ? 'Hide' : 'Show'}
                        </ToggleButton>
                    </PasswordStack>
                </Row>
            </Card>

            <Card
                as={motion.div}
                initial={{ opacity: 0, y: -50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
            >
                <ThreeColRow>
                    <Input
                        type="text"
                        name="firstName"
                        placeholder="First Name"
                        value={formData.firstName}
                        onChange={handleInputChange}
                    />
                    <Input
                        type="text"
                        name="lastName"
                        placeholder="Last Name"
                        value={formData.lastName}
                        onChange={handleInputChange}
                    />
                    <Input
                        type="text"
                        name="nickname"
                        placeholder="Nickname (optional)"
                        value={formData.nickname}
                        onChange={handleInputChange}
                    />
                </ThreeColRow>
            </Card>

            <Card
                as={motion.div}
                initial={{ opacity: 0, y: -50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
            >
                <Stack>
                    <Input
                        type="text"
                        name="billingAddress"
                        placeholder="Billing Address"
                        value={formData.billingAddress}
                        onChange={handleInputChange}
                        style={addressError && !addressValid ? { borderColor: '#d32f2f', boxShadow: '0 0 0 1px #d32f2f' } : undefined}
                    />
                    <Input
                        type="text"
                        name="city"
                        placeholder="City"
                        value={formData.city}
                        onChange={handleInputChange}
                        style={addressError && !addressValid ? { borderColor: '#d32f2f', boxShadow: '0 0 0 1px #d32f2f' } : undefined}
                    />
                    <Input
                        type="text"
                        name="state"
                        placeholder="State"
                        value={formData.state}
                        onChange={handleInputChange}
                        style={addressError && !addressValid ? { borderColor: '#d32f2f', boxShadow: '0 0 0 1px #d32f2f' } : undefined}
                    />
                    <Input
                        type="text"
                        name="zipCode"
                        placeholder="Zip Code"
                        value={formData.zipCode}
                        onChange={handleInputChange}
                        style={addressError && !addressValid ? { borderColor: '#d32f2f', boxShadow: '0 0 0 1px #d32f2f' } : undefined}
                    />
                    {!addressValid && addressError && (
                        <AddressErrorText>{addressError}</AddressErrorText>
                    )}
                </Stack>
            </Card>
            <form onSubmit={(e) => { e.preventDefault(); handleSignUp(); }}>
                <CenteredContainer>
                    {/* Privacy Policy Checkbox and Modal */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '12px',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '15px',
                        color: '#4B5563',
                        flexWrap: 'wrap'
                    }}>
                        <input
                            type="checkbox"
                            id="privacyPolicy"
                            checked={privacyChecked}
                            onChange={e => setPrivacyChecked(e.target.checked)}
                            required
                            style={{ marginRight: '8px' }}
                        />
                        <label htmlFor="privacyPolicy" style={{ cursor: 'pointer', userSelect: 'none' }}>
                            I agree to the{' '}
                            <span
                                style={{ color: '#007AFF', textDecoration: 'underline', cursor: 'pointer' }}
                                onClick={e => { e.preventDefault(); setShowPrivacyModal(true); }}
                                tabIndex={0}
                                role="button"
                                aria-label="Read Privacy Policy"
                            >
                                Privacy Policy
                            </span>
                        </label>
                    </div>
                    {/* Privacy Policy Modal */}
                    {showPrivacyModal && (
                        <div
                            style={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                width: '100vw',
                                height: '100vh',
                                background: 'rgba(0,0,0,0.45)',
                                zIndex: 2000,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            onClick={() => setShowPrivacyModal(false)}
                        >
                            <div
                                style={{
                                    background: 'white',
                                    borderRadius: '10px',
                                    maxWidth: '90vw',
                                    maxHeight: '90vh',
                                    overflowY: 'auto',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                                    padding: '24px',
                                    position: 'relative'
                                }}
                                onClick={e => e.stopPropagation()}
                            >
                                <button
                                    onClick={() => setShowPrivacyModal(false)}
                                    style={{
                                        position: 'absolute',
                                        top: 10,
                                        right: 10,
                                        background: 'none',
                                        border: 'none',
                                        fontSize: '1.5rem',
                                        cursor: 'pointer',
                                        color: '#4B5563'
                                    }}
                                    aria-label="Close Privacy Policy"
                                >
                                    ×
                                </button>
                                <PrivacyPolicy />
                            </div>
                        </div>
                    )}
                    {(selectedMembership === 'Monthly' || selectedMembership === 'Yearly') && (
                        <CenteredText>
                            {selectedMembership === 'Monthly' && (
                                "You will complete your Monthly Subscription in the My Account page after email Confirmation"
                            )}
                            {selectedMembership === 'Yearly' && (
                                "You will complete your Yearly Subscription in the My Account page after email Confirmation"
                            )}
                        </CenteredText>
                    )}
                    {(selectedMembership === 'Monthly' || selectedMembership === 'Yearly' || selectedMembership === 'Free' || selectedMembership === 'Gift') && (
                        <ButtonRow>
                            <SecondaryButton type="button" onClick={() => setCurrentPage('login')}>
                                Back to Login
                            </SecondaryButton>
                            <SubmitButton onClick={handleSignUp} disabled={loading || !privacyChecked}>
                                {loading ? <ClipLoader size={20} color="#FFFFFF" /> : 'Submit'}
                            </SubmitButton>
                        </ButtonRow>
                    )}
                </CenteredContainer>
            </form>
        </Container>
    );
};

// Styled Components
const Container = styled.div`
    background-image: url(${backgroundImage});
    background-size: cover;
    background-position: center;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 20px;
`;

const SubscriptionCard = styled.div`
    width: 100%;
    max-width: 600px;
    background-image: url(${cardBackgroundImage});
    background-size: cover;
    background-position: center;
    border: 2px solid black;
    border-radius: 10px;
    box-shadow: 3px 3px 5px rgba(0, 0, 0, 0.3);
    padding: 20px;
    margin-bottom: 20px;
    text-align: center;
    box-sizing: border-box;

    h3 {
        font-family: 'Times New Roman', Times, serif;
        font-weight: bold;
        font-size: 24px;
        margin-bottom: 20px;
    }

    @media (max-width: 600px) {
        width: 90%;
    }
`;

const Option = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;

    input[type='radio'] {
        accent-color: #4b5563;
        cursor: pointer;
    }

    label {
        font-family: Arial, sans-serif;
        font-size: 14px;
        font-weight: bold;
        color: #4b5563;
        cursor: pointer;
    }

    @media (max-width: 600px) {
        label {
            font-size: 12px;
        }
    }
`;

const Description = styled.div`
    margin-top: 10px;
    font-family: Arial, sans-serif;
    font-size: 14px;
    color: #4b5563;
    border: 1px solid #ccc;
    border-radius: 5px;
    padding: 10px;
    background-color: white;
    text-align: center;

    @media (max-width: 600px) {
        font-size: 12px;
    }
`;

const Card = styled.div`
    width: 100%;
    max-width: 600px;
    background-image: url(${cardBackgroundImage});
    background-size: cover;
    background-position: center;
    border: 2px solid black;
    border-radius: 10px;
    box-shadow: 3px 3px 5px rgba(0, 0, 0, 0.3);
    padding: 20px;
    margin-bottom: 20px;
    box-sizing: border-box;

    @media (max-width: 600px) {
        width: 90%;
    }
`;

const MembershipTitle = styled.h2`
    font-family: 'Times New Roman', Times, serif;
    font-size: 24px;
    font-weight: bold;
    color: #4b5563;
    text-align: center;
    margin-bottom: 20px;

    @media (max-width: 600px) {
        font-size: 20px;
    }
`;

const Row = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 20px;

    @media (max-width: 600px) {
        flex-direction: column;
        gap: 10px;
    }
`;

const Stack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const Input = styled.input`
    width: 100%;
    padding: 10px;
    border: 2px solid black;
    border-radius: 10px;
    font-family: Arial, sans-serif;
    font-size: 14px;
    box-sizing: border-box;

    @media (max-width: 600px) {
        font-size: 12px;
    }
`;

const ToggleButton = styled.button`
    margin-top: 10px;
    align-self: flex-end;
    background: none;
    border: none;
    font-size: 14px;
    cursor: pointer;

    @media (max-width: 600px) {
        font-size: 12px;
    }
`;

const ButtonRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    flex-wrap: wrap;
    margin-top: 10px;
`;

const SecondaryButton = styled.button`
    padding: 10px 20px;
    border: 2px solid #4b5563;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.8);
    font-family: 'Times New Roman', Times, serif;
    font-size: 16px;
    font-weight: bold;
    color: #4b5563;
    cursor: pointer;

    @media (max-width: 600px) {
        width: 90%;
        padding: 8px 16px;
    }
`;

const SubmitButton = styled.button`
    padding: 10px 20px;
    border: 2px solid black;
    border-radius: 10px;
    background-image: url(${buttonImage});
    background-size: cover;
    font-family: 'Times New Roman', Times, serif;
    font-size: 16px;
    font-weight: bold;
    color: white;
    margin: 10px;
    cursor: pointer;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    @media (max-width: 600px) {
        width: 90%;
        padding: 8px 16px;
    }
`;

const AddressErrorText = styled.div`
    font-family: Arial, sans-serif;
    font-size: 13px;
    color: #d32f2f;
    font-weight: bold;
    margin-top: 4px;
`;

const CenteredText = styled.div`
    font-family: Arial, sans-serif;
    font-size: 14px;
    font-weight: bold;
    text-align: center;
    color: #4b5563;
    margin-top: 20px;

    @media (max-width: 600px) {
        font-size: 12px;
    }
`;

const CenteredContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 20px;

    @media (max-width: 600px) {
        width: 90%;
    }
`;

const PasswordStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    align-items: flex-end;
    width: 100%;
`;

const ThreeColRow = styled.div`
    display: flex;
    flex-direction: row;
    gap: 20px;
    width: 100%;
    @media (max-width: 600px) {
        flex-direction: column;
        gap: 10px;
    }
`;

export default SignUp;